import { DurationInputArg2, MomentInput } from 'moment';
import { localTime, machineStatus, ncFileStatuses } from '@helpers';
import { font } from '@styles/fonts';
import { MIN_DISPLAY_BREAKPOINT, TIMELINE_FONT_TYPE } from '@components/machines/components/timeline/constants';
import { CalculateTimelineItemParams, Event } from '@interfaces/timeline';
import { ProductionBatch } from '@interfaces/search';
import i18n from 'i18n-js';
import { RefObject } from 'react';
import { fullHeight } from '@components/frame';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { BatchElement, GroupedJobElement, OptimizedEvent } from '@components/machines/components/timeline/types';
import { EVENT_GROUPING_BREAKPOINT } from '@components/machines/components/timeline/constants';

export const calculateTimelineItem = ({ itemStart, itemEnd, timelineStart, secondWidth }: CalculateTimelineItemParams) => {
  const secondsFromPeriodStart = localTime(itemStart).diff(localTime(timelineStart), 's');
  const elementDuration = localTime(itemEnd).diff(localTime(itemStart), 's');
  const originalWidth = secondWidth * elementDuration;
  // Round values to avoid sub-pixel rendering gaps (for bar rendering)
  const elementWidth = Math.max(Math.ceil(originalWidth), MIN_DISPLAY_BREAKPOINT);
  const elementLeft = Math.floor(secondWidth * secondsFromPeriodStart);

  return { elementWidth, elementLeft, elementDuration, originalWidth };
};

export const getTimelineFontStyles = () => {
  const textStyles = font[TIMELINE_FONT_TYPE];
  const lineHeight = font[TIMELINE_FONT_TYPE].lineHeight;

  const fontStylesWeb = { ...textStyles, lineHeight: `${lineHeight}px` };

  return { fontStylesWeb };
};

export const getBatchStatusText = ({ links, ncFileStatus }: {links: ProductionBatch['links'] | undefined} & Pick<ProductionBatch, 'ncFileStatus'>) => {
  let text = '';

  if (ncFileStatus !== ncFileStatuses.Unknown && links && links?.length > 0) {
    text = 'Linked';
  } else if (ncFileStatus === ncFileStatuses.Unknown) {
    text = 'Unknown NC File';
  } else if (links?.length === 0) {
    text = 'Not Linked';
  }

  return i18n.t(text);
};

export const getTimelineElementBorderRadius = ({
  elementLeft,
  elementWidth,
  blockWidth,
  borderRadius,
}: {
  elementLeft: number;
  elementWidth: number;
  blockWidth: number;
  borderRadius: number;
}) => {
  const isLeftEdge = elementLeft === 0;
  const isAtRightEdge = Math.round(elementLeft + elementWidth) >= blockWidth;

  return {
    ...(isLeftEdge ? { borderTopLeftRadius: borderRadius, borderBottomLeftRadius: borderRadius } : {}),
    ...(isAtRightEdge ? { borderTopRightRadius: borderRadius, borderBottomRightRadius: borderRadius } : {}),
  };
};

export const calculateTimelineBatches = ({
  batches,
  groupedByBatchEvents,
  batchEndField,
  batchStartField,
  secondWidth,
  timelineStart,
}: Pick<CalculateTimelineItemParams, 'secondWidth' | 'timelineStart'> & {
  groupedByBatchEvents: Event[];
  batches: ProductionBatch[];
  batchEndField: keyof Event;
  batchStartField: keyof Event;
}) => {
  let totalDuration = 0; // Total duration for visible Batches (we are not taking into account events which we can't assign batch for)
  let unknownDuration = 0;

  // Create mapping of productionBatchId to batch for O(1) lookup
  const batchesMap = batches.reduce((map, batch) => {
    map[batch.productionBatchId] = batch;
    return map;
  }, {} as Record<string, ProductionBatch>);

  const batchElements = groupedByBatchEvents?.map((b) => {
    const { elementLeft, elementWidth, elementDuration, originalWidth } = calculateTimelineItem({
      itemStart: b[batchStartField] as string,
      itemEnd: b[batchEndField] as string,
      secondWidth,
      timelineStart,
    });

    const batchByProductionBatchId = batchesMap[b.productionBatchId];

    const shouldDisplayUnknown = batchByProductionBatchId?.ncFileStatus === ncFileStatuses.Unknown || batchByProductionBatchId?.links?.length === 0;

    // Accumulate durations for percentage calculation
    if (batchByProductionBatchId) {
      totalDuration += elementDuration;
    }
    
    if (shouldDisplayUnknown) {
      unknownDuration += elementDuration;
    }

    const batchStatusText = getBatchStatusText({
      links: batchByProductionBatchId?.links,
      ncFileStatus: batchByProductionBatchId?.ncFileStatus,
    });

    return {
      b: batchByProductionBatchId,
      elementLeft,
      elementWidth,
      originalWidth,
      shouldDisplayUnknown,
      batchStatusText,
    };
  });

  return { batchElements, totalDuration, unknownDuration };
};

export const getEventsContainerHeight = (eventsContainerRef: RefObject<HTMLDivElement>) => {
  if (eventsContainerRef.current) {
    const rect = eventsContainerRef.current.getBoundingClientRect();
    return fullHeight - rect.top - 20; // 20px for bottom margin
  }
  return 200; // fallback
};

export const isRangeExactUnit = (start: MomentInput, end: MomentInput) => {
  const units = ['year', 'month', 'week', 'day'] as DurationInputArg2[];

  for (const unit of units) {
    if (localTime(start).add(1, unit).isSame(localTime(end))) {
      return unit;
    }
    if (localTime(start).add(1, unit).isSame(localTime(end).add(1, 's'))) {
      return unit;
    }
  }

  return false;
};

export const checkIsMonthSelected = (startTime: MomentInput, endTime: MomentInput) =>
  localTime(startTime).isSame(localTime(startTime).startOf('month'), 'm') &&
  localTime(endTime).isSame(localTime(endTime).endOf('month'), 'm') &&
  localTime(startTime).month() === localTime(endTime).month();

export const formatDateToView = (selectedTime: TimePeriod, startTime: MomentInput, endTimeInitial: MomentInput, isCustomRange: boolean) => {
  let endTime = endTimeInitial;
  const daysDifference = localTime(endTime).diff(startTime, 'days');
  const isStartAndEndSameDate =
    localTime(startTime).format('YYYY-MM-DD') === localTime(endTime).subtract(1, 's').format('YYYY-MM-DD') ||
    localTime(startTime).format('YYYY-MM-DD') === localTime(endTime).format('YYYY-MM-DD');

  const startWeek = localTime(startTime).isoWeek();
  const startYear = localTime(startTime).year();
  const currentWeek = localTime().isoWeek();
  const currentYear = localTime().year();

  const startTimeMonth = i18n.t(localTime(startTime).format('MMM'));
  const startTimeMonthFull = i18n.t(localTime(startTime).format('MMMM'));
  const endTimeMonth = i18n.t(localTime(endTime).format('MMM'));

  const selectedMoreThanMonth = localTime(endTime).diff(localTime(startTime), 'day') > 30;

  const weekDiff = currentWeek - startWeek;
  const isSameYear = startYear === currentYear;
  const isToday = localTime(startTime).isSame(localTime().startOf('day'), 'd');
  const isYesterday = localTime(startTime).isSame(localTime().subtract(1, 'days').startOf('day'), 'd');

  const rangeExactUnit = isRangeExactUnit(startTime, endTime);

  if (isStartAndEndSameDate) {
    if (rangeExactUnit) {
      const baseline = `${localTime(startTime).format('DD')} ${startTimeMonth}`;
      if (isToday) {
        return `${i18n.t('Today')} ${baseline}`;
      }
      if (isYesterday) {
        return `${i18n.t('Yesterday')} ${baseline}`;
      }
      return `${localTime(startTime).format('dddd')} \u2022 ${localTime(startTime).format('D')} ${i18n.t(startTimeMonthFull)} ${startYear}`;
    } else {
      const baseline = `${localTime(startTime).format('DD')} ${startTimeMonth} ${localTime(startTime).format('HH:mm')} - ${localTime(endTime).format(
        'HH:mm'
      )}`;
      if (isToday) {
        return `${i18n.t('Today')} ${baseline}`;
      }
      if (isYesterday) {
        return `${i18n.t('Yesterday')} ${baseline}`;
      }
    }
  }

  if (daysDifference === 7 && !isCustomRange) {
    endTime = localTime(endTimeInitial).subtract(1, 's').format();
    if (weekDiff === 0 && isSameYear) {
      return `${i18n.t('This Week')}`;
    }
    if (weekDiff === 1 && isSameYear) {
      return `${i18n.t('Last Week')} \u2022 ${localTime(startTime).format('DD')} ${startTimeMonth} - ${localTime(endTime).format(
        'DD'
      )} ${endTimeMonth}`;
    }
    return `${i18n.t('Week')} ${startWeek}, ${localTime(startTime).format('YYYY')} \u2022 ${localTime(startTime).format(
      'DD'
    )} ${startTimeMonth} - ${localTime(endTime).format('DD')} ${endTimeMonth}`;
  } else if (rangeExactUnit === 'month') {
    const monthFullName = i18n.t(localTime(startTime).format('MMMM'));
    return `${monthFullName} ${startYear}`;
  } else if (rangeExactUnit === 'year') {
    return startYear;
  } else {
    const monthStart = i18n.t(localTime(startTime).format('MMM'));
    const monthEnd = i18n.t(localTime(endTime).format('MMM'));
    const shouldDisplayTime = !selectedMoreThanMonth || selectedTime !== TimePeriod.YEAR;

    return `${localTime(startTime).format('DD')} ${monthStart} ${!shouldDisplayTime ? '' : localTime(startTime).format('HH:mm')} - ${
      isStartAndEndSameDate ? '' : `${localTime(endTime).format('DD')} ${monthEnd}`
    }${!shouldDisplayTime ? '' : ' ' + localTime(endTime).format('HH:mm')}`;
  }
};

export const groupConsecutiveJobElements = (batchElements: BatchElement[] | undefined): GroupedJobElement[] => {
  if (!batchElements) return [];

  const groups: GroupedJobElement[] = [];
  let currentGroup: BatchElement[] = [];

  for (let i = 0; i < batchElements.length; i++) {
    const element = batchElements[i];

    if (element.shouldDisplayUnknown || !element.b?.links?.length) {
      continue;
    }

    const links = element.b.links;
    const prevElement = currentGroup[currentGroup.length - 1];

    const canGroup =
      prevElement &&
      element.elementLeft - (prevElement.elementLeft + prevElement.elementWidth) < 1 &&
      prevElement.b?.links?.length === links.length &&
      prevElement.b?.links.every(
        (link: any, idx: number) =>
          link.productionOrderNumber === links[idx]?.productionOrderNumber &&
          link.productionOperationNumber === links[idx]?.productionOperationNumber
      );

    if (canGroup) {
      currentGroup.push(element);
    } else {
      if (currentGroup.length > 0) {
        const firstEl = currentGroup[0];
        const lastEl = currentGroup[currentGroup.length - 1];
        const left = firstEl.elementLeft;
        const width = lastEl.elementLeft + lastEl.elementWidth - firstEl.elementLeft;

        groups.push({
          elements: currentGroup,
          left,
          width,
          links: firstEl.b?.links || [],
          shouldDisplayText: width > 3,
        });
      }

      currentGroup = [element];
    }
  }

  if (currentGroup.length > 0) {
    const firstEl = currentGroup[0];
    const lastEl = currentGroup[currentGroup.length - 1];
    const left = firstEl.elementLeft;
    const width = lastEl.elementLeft + lastEl.elementWidth - firstEl.elementLeft;

    groups.push({
      elements: currentGroup,
      left,
      width,
      links: firstEl.b?.links || [],
      shouldDisplayText: width > 3,
    });
  }

  return groups;
};

export const NC_FILE_TYPE_LABELS: Record<string, string> = {
  [ncFileStatuses.Unknown]: 'Unknown',
  [ncFileStatuses.Linked]: 'Linked',
  [ncFileStatuses.Change]: 'Change',
  [ncFileStatuses.Ignored]: 'Ignore',
  [ncFileStatuses.Setup]: 'Setup',
};

export const matchNcFileIcon = (ncFileType: string): string | undefined => {
  switch (ncFileType) {
    case ncFileStatuses.Unknown:
      return 'unknownLink';
    case ncFileStatuses.Linked:
      return 'linked';
    case ncFileStatuses.Change:
      return 'loopedArrows';
    case ncFileStatuses.Ignored:
      return 'crossedBell';
    case ncFileStatuses.Setup:
      return 'setup';
  }
};

export const matchNcFileColor = (ncFileType: string, status?: string): string | undefined => {
  switch (ncFileType) {
    case ncFileStatuses.Unknown:
      return 'System/Light/Yellow';
    case ncFileStatuses.Linked:
      return status === machineStatus.NOTPRODUCTIVE ? 'Status/Light/Not Productive' : 'ProdJobBatch/Light/Running';
    case ncFileStatuses.Change:
      return 'Status/Light/Change';
    case ncFileStatuses.Ignored:
      return 'Status/Light/Not Productive';
    case ncFileStatuses.Setup:
      return 'Status/Light/Setup';
  }
};

export const computeEventsHash = (events: Event[]): string => {
  if (events.length === 0) return '';
  if (events.length === 1) {
    const e = events[0];
    return `${e.startedOn}_${e.endedOn}`;
  }

  const first = events[0];
  const last = events[events.length - 1];
  return `${first.startedOn}_${first.endedOn}|${last.startedOn}_${last.endedOn}`;
};

export const optimizeEventsForRendering = (events: Event[], startTime: MomentInput, secondWidth: number): OptimizedEvent[] => {
  const optimizedEvents: OptimizedEvent[] = [];
  let currentGroup: Event[] = [];
  let groupWidth = 0;
  let groupStartPosition = 0;
  let groupEndPosition = 0;

  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    const elementSeconds = localTime(event.startedOn).diff(localTime(startTime), 's');
    const elementEndSeconds = localTime(event.endedOn).diff(localTime(startTime), 's');
    const eventWidth = (elementEndSeconds - elementSeconds) * secondWidth;

    if (eventWidth >= EVENT_GROUPING_BREAKPOINT) {
      if (currentGroup.length > 0) {
        const finalGroupWidth = (groupEndPosition - groupStartPosition) * secondWidth;
        const largestEvent = currentGroup.reduce((largest, current) => (current.durationInSeconds > largest.durationInSeconds ? current : largest));
        optimizedEvents.push({
          ...largestEvent,
          _optimizedWidth: finalGroupWidth,
          _optimizedPosition: groupStartPosition,
        });

        currentGroup = [];
        groupWidth = 0;
      }

      optimizedEvents.push(event);
    } else {
      if (currentGroup.length === 0) {
        currentGroup = [event];
        groupWidth = eventWidth;
        groupStartPosition = elementSeconds;
        groupEndPosition = elementEndSeconds;
      } else {
        currentGroup.push(event);
        groupEndPosition = elementEndSeconds;
        groupWidth = (groupEndPosition - groupStartPosition) * secondWidth;

        if (groupWidth >= EVENT_GROUPING_BREAKPOINT) {
          const largestEvent = currentGroup.reduce((largest, current) =>
            current.durationInSeconds > largest.durationInSeconds ? current : largest
          );
          optimizedEvents.push({
            ...largestEvent,
            _optimizedWidth: groupWidth,
            _optimizedPosition: groupStartPosition,
          });
          currentGroup = [];
          groupWidth = 0;
        }
      }
    }
  }

  if (currentGroup.length > 0) {
    const finalGroupWidth = (groupEndPosition - groupStartPosition) * secondWidth;
    const largestEvent = currentGroup.reduce((largest, current) => (current.durationInSeconds > largest.durationInSeconds ? current : largest));
    optimizedEvents.push({
      ...largestEvent,
      _optimizedWidth: finalGroupWidth,
      _optimizedPosition: groupStartPosition,
    });
  }

  return optimizedEvents;
};
